// src/lib/auth/authContext.tsx
// Update the User type to correctly handle Discord members with role

'use client';

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useRouter } from 'next/navigation';

// Authentication provider types
export type AuthProvider = 'email' | 'google' | 'discord';

// Membership types with Discord role member
export type MembershipType = 'free' | 'daily' | 'weekend' | 'monthly' | 'discord';

// Subscription status types
export type SubscriptionStatus = 'active' | 'canceled' | 'expired' | null;

// Updated User type to include Discord role information
export type User = {
  id: string;
  email: string;
  username?: string;
  membershipType: MembershipType;
  membershipExpiry?: Date | string | null;
  availableRaces?: number;
  discordServerId?: string;
  discordId?: string;
  googleId?: string;
  subscriptionStatus?: SubscriptionStatus;
  subscriptionId?: string;
  stripeCustomerId?: string;
  // New field to identify Discord members with the member role
  discordMemberRole?: boolean;
};

type AuthContextType = {
  user: User | null;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginWithDiscord: () => Promise<void>;
  register: (email: string, password: string, username: string) => Promise<void>;
  logout: () => void;
  checkAccess: (raceId: string) => Promise<{
    hasAccess: boolean;
    canUseFreeTrial?: boolean;
  }>;
  purchasePlan: (planType: MembershipType) => Promise<void>;
  purchaseRace: (raceId: string) => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      setIsLoading(true);
      try {
        // Get token from localStorage
        const token = localStorage.getItem('token');
        if (token) {
          // Get user data if token exists
          const userData = await fetchCurrentUser(token);
          
          // Process Discord member role if applicable
          if (userData.discordId) {
            // Check if the user has Discord member role information
            // If discordMemberRole is set in the API response, use it
            // Otherwise, default to treating all Discord users as members
            if (userData.discordMemberRole === undefined) {
              userData.discordMemberRole = true;
            }
            
            // Set membershipType to 'discord' for Discord members with the role
            if (userData.discordMemberRole) {
              userData.membershipType = 'discord';
            }
          }
          
          setUser(userData);
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
        localStorage.removeItem('token');
        setError('Authentication failed. Please log in again.');
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  // Fetch current user function
  const fetchCurrentUser = async (token: string): Promise<User> => {
    const response = await fetch('/api/auth/me', {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch user data');
    }

    const data = await response.json();
    return data.user;
  };

  // Rest of the auth context implementation...
  // Email login
  const login = async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Login failed');
      }

      const data = await response.json();
      
      // Save token
      localStorage.setItem('token', data.token);
      
      // Process Discord member role if applicable
      if (data.user.discordId) {
        if (data.user.discordMemberRole === undefined) {
          data.user.discordMemberRole = true;
        }
        
        if (data.user.discordMemberRole) {
          data.user.membershipType = 'discord';
        }
      }
      
      setUser(data.user);
      
      // Redirect to home after login
      router.push('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to login');
    } finally {
      setIsLoading(false);
    }
  };

  // Google login
  const loginWithGoogle = async () => {
    // Redirect to Google OAuth
    window.location.href = '/api/auth/google';
  };

  // Discord login
  const loginWithDiscord = async () => {
    // Redirect to Discord OAuth
    window.location.href = '/api/auth/discord';
  };

  // Register new user
  const register = async (email: string, password: string, username: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, username }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Registration failed');
      }

      const data = await response.json();
      
      // Save token
      localStorage.setItem('token', data.token);
      setUser(data.user);
      
      // Redirect to home after registration
      router.push('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to register');
    } finally {
      setIsLoading(false);
    }
  };

  // Logout
  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    router.push('/login');
  };

  // Check race access
  const checkAccess = async (raceId: string) => {
    if (!user) return { hasAccess: false };
    
    // Discord members always have access
    if (user.membershipType === 'discord' || user.discordMemberRole) {
      return { hasAccess: true };
    }
    
    // Check access based on membership type
    try {
      const response = await fetch(`/api/membership/access/${raceId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) return { hasAccess: false };
      
      const data = await response.json();
      return data;
    } catch (err) {
      console.error('Access check error:', err);
      return { hasAccess: false };
    }
  };

  const purchasePlan = async (planType: MembershipType) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/subscription/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ 
          planType, 
          // Add more context like current user info
          userId: user?.id 
        }),
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '決済の初期化に失敗しました');
      }
  
      const data = await response.json();
      
      // Log purchase attempt for tracking
      console.log(`Initiating ${planType} plan purchase`);
      
      // More robust location change
      if (data.url) {
        window.location.assign(data.url);
      } else {
        throw new Error('決済URLが見つかりません');
      }
    } catch (err) {
      console.error('Purchase Plan Error:', err);
      setError(err instanceof Error ? err.message : '決済処理に失敗しました');
      setIsLoading(false);
    }
  };
// Purchase individual race - Square決済用に更新
const purchaseRace = async (raceId: string) => {
  setIsLoading(true);
  setError(null);
  
  try {
    const response = await fetch('/api/subscription/purchase-race', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({ raceId }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'レース購入の処理に失敗しました');
    }

    const data = await response.json();
    
    // トラッキングや分析のためにイベントを記録することもできます
    console.log(`レース ${raceId} の購入を開始しました`);
    
    // Squareの決済ページにリダイレクト
    window.location.href = data.url;
  } catch (err) {
    setError(err instanceof Error ? err.message : 'レース購入に失敗しました');
    setIsLoading(false);
  }
};
  return (
    <AuthContext.Provider value={{
      user,
      isLoading,
      error,
      login,
      loginWithGoogle,
      loginWithDiscord,
      register,
      logout,
      checkAccess,
      purchasePlan,
      purchaseRace
    }}>
      {children}
    </AuthContext.Provider>
  );
};


